import { UseFormReturn } from 'react-hook-form';
import {
  Form,
  FormField,
  FormItem,
  FormMessage,
} from '@web/components/ui/form';
import { InputAnimatedLabel } from '@web/components/ui/input-animated-label';
import { CategorySelectData, ProcedureEditData } from '@shared/types';
import { ProcedureCategoryDropdown } from '../common/procedure-category-dropdown';
import { ProcedureRiskLevelDropdown } from '../common/procedure-riskLevel-dropdown';
import { ProcedureStatusDropdown } from '../common/procedure-status-dropdown';

type ProcedureDetailsFormProps = {
  form: UseFormReturn<ProcedureEditData>;
  onSubmit: (data: ProcedureEditData) => Promise<void>;
  isEditMode: boolean;
  categoryList: CategorySelectData[];
  setIsSubmitting: (isSubmitting: boolean) => void;
};

export function ProcedureDetailsForm({
  form,
  onSubmit,
  isEditMode,
  categoryList,
  setIsSubmitting,
}: Readonly<ProcedureDetailsFormProps>) {
  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          setIsSubmitting(true);

          void form.handleSubmit(onSubmit)(e);
        }}
      >
        <div className="grid gap-6 md:grid-cols-2">
          {/* Procedure Fields */}
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <InputAnimatedLabel
                  label="Code"
                  {...field}
                  readOnly={!isEditMode}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <InputAnimatedLabel
                  label="Name"
                  {...field}
                  readOnly={!isEditMode}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="displayName"
            render={({ field }) => (
              <FormItem>
                <InputAnimatedLabel
                  label="Display Name"
                  {...field}
                  readOnly={!isEditMode}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Financial Fields */}
          <FormField
            control={form.control}
            name="price"
            render={({ field: { onChange, ...field } }) => (
              <FormItem>
                <InputAnimatedLabel
                  label="Price"
                  type="number"
                  {...field}
                  onChange={(e) => {
                    onChange(Number(e.target.value));
                  }}
                  readOnly={!isEditMode}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <ProcedureCategoryDropdown
                  categorysList={categoryList}
                  value={field.value}
                  readOnly={!isEditMode}
                  onSelect={(category) => {
                    field.onChange(category);
                  }}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="duration"
            render={({ field: { onChange, ...field } }) => (
              <FormItem>
                <InputAnimatedLabel
                  label="Duration (minutes)"
                  type="number"
                  {...field}
                  onChange={(e) => {
                    onChange(Number(e.target.value));
                  }}
                  readOnly={!isEditMode}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Additional Fields */}
          <FormField
            control={form.control}
            name="riskLevel"
            render={({ field }) => (
              <FormItem>
                <ProcedureRiskLevelDropdown
                  value={field.value}
                  readOnly={!isEditMode}
                  onSelect={(status) => {
                    form.setValue('riskLevel', status);
                  }}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <ProcedureStatusDropdown
                  value={field.value}
                  readOnly={!isEditMode}
                  onSelect={(status) => {
                    form.setValue('status', status);
                  }}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Description Fields */}
          <FormField
            control={form.control}
            name="shortDescription"
            render={({ field }) => (
              <FormItem>
                <InputAnimatedLabel
                  inputType="textarea"
                  maxHeight={150}
                  minHeight={100}
                  label="Short Description"
                  {...field}
                  readOnly={!isEditMode}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <InputAnimatedLabel
                  label="Description"
                  {...field}
                  readOnly={!isEditMode}
                  inputType="textarea"
                  maxHeight={150}
                  minHeight={100}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="preparationInstructions"
            render={({ field }) => (
              <FormItem>
                <InputAnimatedLabel
                  label="Preparation Instructions"
                  {...field}
                  readOnly={!isEditMode}
                  inputType="textarea"
                  maxHeight={150}
                  minHeight={100}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="aftercareInstructions"
            render={({ field }) => (
              <FormItem>
                <InputAnimatedLabel
                  label="Aftercare Instructions"
                  {...field}
                  readOnly={!isEditMode}
                  inputType="textarea"
                  maxHeight={150}
                  minHeight={100}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <button type="submit" className="hidden" />
      </form>
    </Form>
  );
}
